import React, { useState, useRef, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SeverityChip } from 'components/shared/SeverityChip';
import { StatusChip } from 'components/shared/StatusChip';
import { ErrorState } from 'components/shared/ErrorState';
import { useEventDetail } from '../../hooks/useEventDetail';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import PromoteEventDialog from '../PromoteEventDialog';

interface EventDetailProps {
  eventId: number | null;
  siteId: number | null;
  open: boolean;
  onClose: () => void;
  onStatusChange?: () => void;
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function EventDetail({ eventId, siteId, open, onClose, onStatusChange }: EventDetailProps) {
  const { event, loading, error, updateStatus, refetch } = useEventDetail({
    eventId,
    siteId,
    enabled: open && eventId !== null,
  });

  const [showLiveView, setShowLiveView] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const handleStatusChange = async (status: 'new' | 'acknowledged' | 'dismissed' | 'promoted') => {
    await updateStatus(status);
    onStatusChange?.();
  };

  const handleFullscreen = useCallback(() => {
    const el = videoContainerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  const handlePromoteClick = () => {
    setPromoteDialogOpen(true);
  };

  const handlePromoteClose = useCallback((result?: { promoted: true; eventId: number }) => {
    setPromoteDialogOpen(false);
    if (result?.promoted) {
      // Dialog already called updateEventById('promoted') and created the incident.
      // Refetch the event to reflect the new status in the drawer.
      refetch();
      onStatusChange?.();
    }
  }, [refetch, onStatusChange]);

  // Parse confidence from details
  let confidence: number | null = null;
  if (event?.details) {
    try {
      const details = JSON.parse(event.details);
      confidence = details.confidence;
    } catch (err) {
      console.warn('[EventDetail] Failed to parse event details:', err);
    }
  }

  // Build clip time range from starts_at
  let clipStartTime: string | null = null;
  let clipEndTime: string | null = null;
  if (event?.starts_at) {
    const startsAt = new Date(event.starts_at);
    const clipStart = new Date(startsAt.getTime() - 30000);
    const clipEnd = new Date(startsAt.getTime() + 120000);
    clipStartTime = clipStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    clipEndTime = clipEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  const getClipUrl = () => event?.video_clip_url || null;

  const getLiveUrl = () => null; // Video unavailable until go2rtc migration

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500, md: 600 },
          maxWidth: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Box>
          <Typography variant="h6">
            Event #{eventId}
          </Typography>
          {event && (
            <Typography variant="body2" color="text.secondary">
              {event.summary}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {loading && !event && (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 1 }} />
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
            </Box>
          </Box>
        )}

        {error && (
          <Box sx={{ m: 2 }}>
            <ErrorState
              title="Failed to load event"
              message={error}
              size="small"
            />
          </Box>
        )}

        {event && (
          <>
            {/* Video Player Section */}
            <Box ref={videoContainerRef} sx={{ bgcolor: 'black', position: 'relative' }}>
              <Box sx={{ aspectRatio: '16/9' }}>
                {showLiveView && getLiveUrl() ? (
                  <VideoPlayer
                    streamUrl={getLiveUrl()!}
                    streamType="hls"
                    poster={event.thumbnail_url || undefined}
                  />
                ) : getClipUrl() ? (
                  <Box
                    component="video"
                    src={getClipUrl()!}
                    poster={event.thumbnail_url || undefined}
                    controls
                    autoPlay
                    muted
                    playsInline
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', bgcolor: 'common.black' }}
                  />
                ) : event.thumbnail_url ? (
                  <Box
                    component="img"
                    src={event.thumbnail_url}
                    alt="Event thumbnail"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.900',
                    }}
                  >
                    <Typography color="grey.500">No video available</Typography>
                  </Box>
                )}
              </Box>

              {/* Video Controls */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: 'grey.900',
                }}
              >
                {clipStartTime && (
                  <Typography variant="caption" color="grey.400">
                    Clip: {clipStartTime}{clipEndTime && ` - ${clipEndTime}`}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  startIcon={<LiveTvIcon />}
                  onClick={() => setShowLiveView(!showLiveView)}
                  sx={{
                    color: showLiveView ? 'error.main' : 'grey.400',
                    borderColor: showLiveView ? 'error.main' : 'grey.600',
                  }}
                  variant="outlined"
                >
                  Live
                </Button>
                <IconButton size="small" sx={{ color: 'grey.400' }} onClick={handleFullscreen}>
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Details Section */}
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{event.event_type}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Confidence</Typography>
                  <Typography variant="body2">
                    {confidence !== null ? `${Math.round(confidence * 100)}%` : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Severity</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {event.severity && (
                      <SeverityChip severity={event.severity} />
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip status={event.status} type="alert" />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Camera</Typography>
                  <Typography variant="body2">
                    {event.camera_display_name || event.camera_name || 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Detected</Typography>
                  <Typography variant="body2">{formatDateTime(event.starts_at || event.created_at)}</Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Actions — state-dependent buttons */}
            <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* new: [Acknowledge] [Dismiss] [Promote to Incident] */}
              {event.status === 'new' && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleStatusChange('acknowledged')}
                  >
                    Acknowledge
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    startIcon={<BlockIcon />}
                    onClick={() => handleStatusChange('dismissed')}
                  >
                    Dismiss
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="info"
                    startIcon={<ArrowUpwardIcon />}
                    onClick={handlePromoteClick}
                  >
                    Promote to Incident
                  </Button>
                </>
              )}

              {/* acknowledged: [Dismiss] [Promote to Incident] */}
              {event.status === 'acknowledged' && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    startIcon={<BlockIcon />}
                    onClick={() => handleStatusChange('dismissed')}
                  >
                    Dismiss
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="info"
                    startIcon={<ArrowUpwardIcon />}
                    onClick={handlePromoteClick}
                  >
                    Promote to Incident
                  </Button>
                </>
              )}

              {/* dismissed: terminal — no actions */}
              {event.status === 'dismissed' && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  This event has been dismissed.
                </Typography>
              )}

              {/* promoted: terminal — show link to incident */}
              {event.status === 'promoted' && (
                <Button
                  variant="outlined"
                  size="small"
                  color="info"
                  startIcon={<OpenInNewIcon />}
                  disabled
                >
                  Promoted to Incident
                </Button>
              )}
            </Box>

          </>
        )}
      </Box>

      {/* Promote to Incident dialog */}
      {event && (
        <PromoteEventDialog
          open={promoteDialogOpen}
          onClose={handlePromoteClose}
          event={{
            eventId: event.id,
            eventType: event.event_type,
            severity: (event.severity as 'critical' | 'high') || 'high',
            summary: event.summary,
            detectionCount: 0,
            addedAt: new Date(event.created_at),
          }}
          cameraName={event.camera_display_name || event.camera_name || 'Unknown'}
          siteName={event.site_display_name || event.site_name || 'Unknown'}
          siteId={event.site_id}
          cameraId={event.camera_id ?? 0}
        />
      )}
    </Drawer>
  );
}

export default EventDetail;
